/* Prescott Honey Farms — brand JS for the Local theme.
 *
 * Self-contained IIFE. Every setup function returns early if its target
 * elements don't exist on the current page. Nothing here touches Local's
 * UI (header, mobile menu, footer, search, cart) — those are Local's job.
 */
(function () {
  'use strict';

  // ---- Hero word stagger ------------------------------------------------
  // Animation delays are now rendered server-side in phf-hero.liquid so the
  // stagger is correct from the first paint. This function is kept as a
  // no-op so existing call sites don't error.
  function staggerHero() {}

  // ---- Honeycomb hero background ---------------------------------------
  // Listens on the .hero parent (not the honeycomb-bg) so mouse events
  // over the centered text and logo card still light up the cells beneath.
  // Cells fade their glow based on distance to the cursor for a polished
  // soft-trail effect, and rebuild on resize.
  function setupHoneycomb() {
    var bg = document.getElementById('honeycombBg');
    if (!bg) return;
    var hero = bg.closest('.hero') || bg.parentElement;
    if (!hero) return;

    var cellW = 50, cellH = 57.7, gap = 4;
    var glowRadius = 130; // px — radius of the cursor glow

    function build() {
      bg.innerHTML = '';
      var rect = bg.getBoundingClientRect();
      var w = rect.width || window.innerWidth;
      var h = rect.height || bg.offsetHeight || window.innerHeight * 0.9;

      // One extra column on each side so odd rows reach the right edge symmetrically
      var step = cellW + gap;
      var cols = Math.ceil(w / step) + 3;
      var rows = Math.ceil(h / (cellH * 0.75 + gap)) + 2;
      // Shift the whole grid LEFT by half a step so even rows overflow left
      // (clipped by overflow:hidden) and the right edge looks balanced
      var startX = -step / 2;

      var frag = document.createDocumentFragment();
      for (var r = 0; r < rows; r++) {
        for (var c = 0; c < cols; c++) {
          var cell = document.createElement('div');
          cell.className = 'honeycomb-cell';
          var x = startX + c * step + (r % 2 ? step / 2 : 0);
          var y = r * (cellH * 0.75);
          cell.style.left = x + 'px';
          cell.style.top = y + 'px';
          // Pre-cache center coords so we don't parseFloat in the hot loop
          cell._cx = x + cellW / 2;
          cell._cy = y + cellH / 2;
          frag.appendChild(cell);
        }
      }
      bg.appendChild(frag);
    }
    build();
    var resizeT;
    window.addEventListener('resize', function () {
      clearTimeout(resizeT);
      resizeT = setTimeout(build, 120);
    });

    var rafPending = false;
    var lastMx = -9999, lastMy = -9999;

    function applyGlow() {
      rafPending = false;
      var rect = bg.getBoundingClientRect();
      var mx = lastMx - rect.left;
      var my = lastMy - rect.top;
      var cells = bg.children;
      for (var i = 0; i < cells.length; i++) {
        var cell = cells[i];
        var dx = mx - cell._cx;
        var dy = my - cell._cy;
        var dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < glowRadius) {
          // Quadratic falloff — close cells glow more, far cells barely
          var t = 1 - dist / glowRadius;
          cell.style.setProperty('--phf-glow', (t * t).toFixed(3));
          cell.classList.add('lit');
        } else if (cell.classList.contains('lit')) {
          cell.style.setProperty('--phf-glow', '0');
          cell.classList.remove('lit');
        }
      }
    }

    hero.addEventListener('mousemove', function (e) {
      lastMx = e.clientX;
      lastMy = e.clientY;
      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(applyGlow);
      }
    });

    hero.addEventListener('mouseleave', function () {
      lastMx = -9999;
      lastMy = -9999;
      var lit = bg.querySelectorAll('.honeycomb-cell.lit');
      for (var i = 0; i < lit.length; i++) {
        lit[i].style.setProperty('--phf-glow', '0');
        lit[i].classList.remove('lit');
      }
    });
  }

  // ---- Reveal on scroll -----------------------------------------------
  function setupReveal() {
    var els = document.querySelectorAll('.phf-section .reveal');
    if (!els.length) return;
    // Mark body so the reveal CSS becomes active (defaults are visible)
    document.body.setAttribute('data-phf-anim', '');
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    els.forEach(function (el) { io.observe(el); });
  }

  // ---- Stat counters ---------------------------------------------------
  function animateCount(el) {
    var target = parseInt(el.dataset.count, 10);
    if (isNaN(target)) return;
    var suffix = el.dataset.suffix || '';
    var duration = 1800;
    var start = performance.now();
    function tick(now) {
      var elapsed = now - start;
      var progress = Math.min(elapsed / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = Math.floor(target * eased) + suffix;
      if (progress < 1) requestAnimationFrame(tick);
      else el.textContent = target + suffix;
    }
    requestAnimationFrame(tick);
  }
  function setupCounters() {
    var els = document.querySelectorAll('.phf-section [data-count]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(animateCount);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          animateCount(entry.target);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });
    els.forEach(function (el) { io.observe(el); });
  }

  // ---- Timeline scroll progress ---------------------------------------
  function setupTimeline() {
    var timeline = document.getElementById('timeline');
    var progress = document.getElementById('timelineProgress');
    if (!timeline || !progress) return;
    var items = timeline.querySelectorAll('.timeline-item');
    function update() {
      var rect = timeline.getBoundingClientRect();
      var winH = window.innerHeight;
      var startPos = winH * 0.6;
      var total = rect.height;
      var scrolled = 0;
      if (rect.top < startPos) scrolled = Math.min(startPos - rect.top, total);
      progress.style.height = Math.min((scrolled / total) * 100, 100) + '%';
      items.forEach(function (item) {
        var ir = item.getBoundingClientRect();
        item.classList.toggle('active', ir.top < winH * 0.6);
      });
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  // ---- Cart drawer refresh helper (shared by all PHF add-to-cart paths) ----
  // Fetches Local's helper-cart section with a cache-buster, parses the
  // response, and applies it to every drawer/header element Local renders:
  //   #AjaxCartForm        — the items list inside the drawer
  //   #AjaxCartSubtotal    — the subtotal row at the bottom of the drawer
  //   [data-header-cart-count]  — every cart count badge in the header
  //   [data-header-cart-total]  — every cart total in the header
  // Then opens the drawer if Local has one mounted on the current page.
  // Returns a Promise so callers can chain UI feedback after.
  function phfRefreshCartUI() {
    return fetch('?section_id=helper-cart&_phf=' + Date.now(), {
      headers: { 'Cache-Control': 'no-cache' }
    })
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');

        var newCartForm  = doc.getElementById('AjaxCartForm');
        var liveCartForm = document.getElementById('AjaxCartForm');
        if (newCartForm && liveCartForm) {
          liveCartForm.innerHTML = newCartForm.innerHTML;
          if (typeof liveCartForm.ajaxifyCartItems === 'function') {
            liveCartForm.ajaxifyCartItems();
          }
        }

        var newSubtotal  = doc.getElementById('AjaxCartSubtotal');
        var liveSubtotal = document.getElementById('AjaxCartSubtotal');
        if (newSubtotal && liveSubtotal) {
          liveSubtotal.innerHTML = newSubtotal.innerHTML;
        }

        var newCountEl = doc.querySelector('[data-cart-count]');
        var newTotalEl = doc.querySelector('[data-cart-total]');
        if (newCountEl) {
          document.querySelectorAll('[data-header-cart-count]').forEach(function (el) {
            el.textContent = newCountEl.textContent;
          });
        }
        if (newTotalEl) {
          document.querySelectorAll('[data-header-cart-total]').forEach(function (el) {
            el.textContent = newTotalEl.textContent;
          });
        }

        var drawer = document.querySelector('[data-js-site-cart-sidebar]');
        if (drawer && typeof drawer.show === 'function') {
          try { drawer.show(); } catch (e) { /* ignore */ }
        }

        try {
          if (window.KROWN && KROWN.functions && KROWN.functions.eventDispatcher) {
            KROWN.functions.eventDispatcher('krown:cart:add', { source: 'phf' });
          } else {
            document.dispatchEvent(new CustomEvent('krown:cart:add', { detail: { source: 'phf' } }));
          }
        } catch (e) { /* ignore */ }
      });
  }

  // ---- AJAX add-to-cart for PHF product cards (homepage + collection grids) ----
  function setupCart() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.phf-section [data-add-to-cart]');
      if (!btn) return;
      e.preventDefault();
      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;
      var originalLabel = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function () { return phfRefreshCartUI(); })
        .then(function () {
          btn.textContent = 'Added ✓';
          setTimeout(function () { btn.textContent = originalLabel; btn.disabled = false; }, 1400);
        })
        .catch(function () {
          btn.textContent = 'Try again';
          setTimeout(function () { btn.textContent = originalLabel; btn.disabled = false; }, 1800);
        });
    });
  }

  // ---- Boot -----------------------------------------------------------
  // ---- Custom dropdown (variant picker on product page) ----------------
  // Replaces the native <select> with a styled button + animated menu.
  // The native select stays in the DOM so form submission still works and
  // assistive tech can fall back to it if needed. Selecting an option in
  // the custom UI updates the native select and dispatches a 'change'
  // event so existing variant-update logic (price, availability) responds.
  function setupDropdowns() {
    var dropdowns = document.querySelectorAll('[data-phf-dropdown]');
    if (!dropdowns.length) return;

    var openDropdown = null;
    var closeTimeouts = new WeakMap();

    function closeOne(dd) {
      if (!dd) return;
      var trigger = dd.querySelector('[data-phf-dropdown-trigger]');
      var menu    = dd.querySelector('[data-phf-dropdown-menu]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (menu) {
        menu.removeAttribute('data-open');
        // Hide after the transition finishes so the fade-out completes
        clearTimeout(closeTimeouts.get(dd));
        var t = setTimeout(function () {
          if (trigger && trigger.getAttribute('aria-expanded') === 'false') {
            menu.setAttribute('hidden', '');
          }
        }, 240);
        closeTimeouts.set(dd, t);
      }
      dd.querySelectorAll('[data-phf-dropdown-option]').forEach(function (o) {
        o.classList.remove('is-active');
      });
      if (openDropdown === dd) openDropdown = null;
    }

    dropdowns.forEach(function (dd) {
      var trigger = dd.querySelector('[data-phf-dropdown-trigger]');
      var menu    = dd.querySelector('[data-phf-dropdown-menu]');
      var native  = dd.querySelector('select.phf-dropdown-native');
      var valueEl = dd.querySelector('[data-phf-dropdown-value]');
      var options = dd.querySelectorAll('[data-phf-dropdown-option]');
      if (!trigger || !menu || !native || !options.length) return;

      // Mark as enhanced so CSS hides the native select and shows the custom UI
      dd.setAttribute('data-phf-enhanced', '');

      function open() {
        if (openDropdown && openDropdown !== dd) closeOne(openDropdown);
        clearTimeout(closeTimeouts.get(dd));
        menu.removeAttribute('hidden');
        // Force a reflow so the transition triggers reliably
        void menu.offsetWidth;
        menu.setAttribute('data-open', 'true');
        trigger.setAttribute('aria-expanded', 'true');
        var pre = dd.querySelector('.phf-dropdown-option.is-selected') || options[0];
        if (pre) {
          options.forEach(function (o) { o.classList.remove('is-active'); });
          pre.classList.add('is-active');
          pre.focus();
        }
        openDropdown = dd;
      }

      function close() { closeOne(dd); }

      function selectOption(opt) {
        var value = opt.getAttribute('data-value');
        if (native.value !== value) {
          native.value = value;
          native.dispatchEvent(new Event('change', { bubbles: true }));
        }
        options.forEach(function (o) {
          var match = (o === opt);
          o.classList.toggle('is-selected', match);
          o.setAttribute('aria-selected', match ? 'true' : 'false');
        });
        if (valueEl) valueEl.textContent = opt.textContent.trim();
        close();
        trigger.focus();
      }

      function moveActive(delta) {
        var current = dd.querySelector('.phf-dropdown-option.is-active');
        var arr = Array.prototype.slice.call(options);
        var idx = arr.indexOf(current);
        if (idx < 0) idx = 0;
        idx = (idx + delta + arr.length) % arr.length;
        arr.forEach(function (o) { o.classList.remove('is-active'); });
        arr[idx].classList.add('is-active');
        arr[idx].focus();
      }

      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        if (trigger.getAttribute('aria-expanded') === 'true') close();
        else open();
      });

      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          open();
        }
      });

      options.forEach(function (opt) {
        opt.addEventListener('click', function (e) { e.stopPropagation(); selectOption(opt); });
        opt.addEventListener('mouseenter', function () {
          options.forEach(function (o) { o.classList.remove('is-active'); });
          opt.classList.add('is-active');
        });
        opt.addEventListener('keydown', function (e) {
          switch (e.key) {
            case 'Enter':
            case ' ':
              e.preventDefault();
              selectOption(opt);
              break;
            case 'Escape':
              e.preventDefault();
              close();
              trigger.focus();
              break;
            case 'ArrowDown':
              e.preventDefault();
              moveActive(1);
              break;
            case 'ArrowUp':
              e.preventDefault();
              moveActive(-1);
              break;
            case 'Home':
              e.preventDefault();
              options.forEach(function (o) { o.classList.remove('is-active'); });
              options[0].classList.add('is-active');
              options[0].focus();
              break;
            case 'End':
              e.preventDefault();
              options.forEach(function (o) { o.classList.remove('is-active'); });
              options[options.length - 1].classList.add('is-active');
              options[options.length - 1].focus();
              break;
          }
        });
      });
    });

    // Close on outside click
    document.addEventListener('click', function (e) {
      if (openDropdown && !openDropdown.contains(e.target)) closeOne(openDropdown);
    });

    // Close on Escape from anywhere
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && openDropdown) {
        var t = openDropdown.querySelector('[data-phf-dropdown-trigger]');
        closeOne(openDropdown);
        if (t) t.focus();
      }
    });
  }

  // ---- PHF product page: AJAX add-to-cart + Local drawer ---------------
  // Mirrors Local theme's own <product-form> flow exactly to avoid two
  // race conditions we hit before:
  //
  //   1. Calling window.refreshCart() in a separate Promise chain meant
  //      the helper-cart section was fetched in parallel with Shopify's
  //      cart commit, sometimes returning a stale "empty cart" render.
  //   2. Without a cache-buster on the section URL, Shopify's edge cache
  //      could serve a stale render for the same path.
  //
  // New flow: chain everything in one Promise — POST cart/add.js, then
  // fetch helper-cart with a unique query param, parse, and apply the
  // result (cart items + subtotal + header count + drawer open) directly
  // to the live DOM. Same elements Local uses (#AjaxCartForm,
  // #AjaxCartSubtotal, [data-header-cart-count/total]).
  function setupPhfProductForm() {
    var form = document.querySelector('[data-phf-product] form.phf-product-form');
    if (!form) return;
    var btn = form.querySelector('[data-phf-buy]');
    var label = form.querySelector('[data-phf-buy-label]');
    if (!btn) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (btn.disabled) return;
      var originalLabel = label ? label.textContent : btn.textContent;

      btn.disabled = true;
      if (label) label.textContent = 'Adding…';
      else btn.textContent = 'Adding…';

      var formData = new FormData(form);

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(function (err) {
              throw new Error(err.description || err.message || 'add failed');
            });
          }
          return r.json();
        })
        .then(function () { return phfRefreshCartUI(); })
        .then(function () {
          if (label) label.textContent = 'Added ✓';
          else btn.textContent = 'Added ✓';
          setTimeout(function () {
            if (label) label.textContent = originalLabel;
            else btn.textContent = originalLabel;
            btn.disabled = false;
          }, 1500);
        })
        .catch(function () {
          if (label) label.textContent = 'Try again';
          else btn.textContent = 'Try again';
          setTimeout(function () {
            if (label) label.textContent = originalLabel;
            else btn.textContent = originalLabel;
            btn.disabled = false;
          }, 2000);
        });
    });
  }

  // ---- Product detail page ---------------------------------------------
  // Wires up two interactions on the PHF product page:
  //   1. Thumbnail click swaps the main image's src/alt.
  //   2. Option <select> change picks the matching variant from the
  //      embedded variant JSON, updates the hidden variant id, the
  //      visible price, and the buy-button availability.
  function setupProduct() {
    var root = document.querySelector('[data-phf-product]');
    if (!root) return;

    // Thumbnails -> swap the main image
    var thumbs = root.querySelectorAll('[data-phf-thumb]');
    var mainImg = root.querySelector('.phf-product-main-img');
    if (mainImg && thumbs.length) {
      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var src = thumb.getAttribute('data-media-src');
          var thumbImg = thumb.querySelector('img');
          if (src) mainImg.setAttribute('src', src);
          if (thumbImg && thumbImg.alt) mainImg.setAttribute('alt', thumbImg.alt);
          mainImg.removeAttribute('srcset');
          mainImg.setAttribute('data-media-id', thumb.getAttribute('data-media-id') || '');
          thumbs.forEach(function (t) { t.classList.remove('is-active'); });
          thumb.classList.add('is-active');
        });
      });
    }

    // Variant switching from embedded variants JSON
    var variantsEl = root.querySelector('script[data-phf-variants]');
    if (!variantsEl) return;
    var variants;
    try { variants = JSON.parse(variantsEl.textContent); } catch (e) { return; }
    if (!Array.isArray(variants) || variants.length === 0) return;

    var optionSelects = root.querySelectorAll('[data-phf-option]');
    var hiddenId      = root.querySelector('[data-phf-variant-id]');
    var priceEl       = root.querySelector('[data-phf-current-price]');
    var compareEl     = root.querySelector('[data-phf-compare-at]');
    var stockEl       = root.querySelector('[data-phf-stock]');
    var buyBtn        = root.querySelector('[data-phf-buy]');
    var buyLabel      = root.querySelector('[data-phf-buy-label]');
    if (!optionSelects.length) return;

    function formatMoney(cents) {
      // Match Shopify's default money formatter for USD; falls back
      // gracefully if shop.money_format isn't standard.
      var dollars = (cents / 100);
      return '$' + dollars.toFixed(2);
    }

    function pickVariant() {
      var picked = [];
      optionSelects.forEach(function (sel) { picked.push(sel.value); });
      var match = variants.find(function (v) {
        for (var i = 0; i < picked.length; i++) {
          if (v['option' + (i + 1)] !== picked[i]) return false;
        }
        return true;
      });
      if (!match) return;

      if (hiddenId) hiddenId.value = match.id;
      if (priceEl) priceEl.textContent = formatMoney(match.price);
      if (compareEl) {
        if (match.compare_at_price && match.compare_at_price > match.price) {
          compareEl.textContent = formatMoney(match.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
      if (buyBtn && buyLabel) {
        if (match.available) {
          buyBtn.disabled = false;
          if (buyLabel.dataset.originalLabel) {
            buyLabel.textContent = buyLabel.dataset.originalLabel;
          }
        } else {
          buyBtn.disabled = true;
          if (!buyLabel.dataset.originalLabel) {
            buyLabel.dataset.originalLabel = buyLabel.textContent;
          }
          buyLabel.textContent = 'Sold Out';
        }
      }
      if (stockEl) {
        stockEl.innerHTML = match.available
          ? '<span class="phf-product-stock-dot" aria-hidden="true"></span>In stock'
          : 'Sold out';
      }
    }

    optionSelects.forEach(function (sel) {
      sel.addEventListener('change', pickVariant);
    });
  }

  function init() {
    try { staggerHero(); }    catch (e) { /* never crash Local */ }
    try { setupHoneycomb(); } catch (e) { /* */ }
    try { setupReveal(); }    catch (e) { /* */ }
    try { setupCounters(); }  catch (e) { /* */ }
    try { setupTimeline(); }  catch (e) { /* */ }
    try { setupCart(); }      catch (e) { /* */ }
    try { setupProduct(); }   catch (e) { /* */ }
    try { setupDropdowns(); } catch (e) { /* */ }
    try { setupPhfProductForm(); } catch (e) { /* */ }
    try { setupYards(); }     catch (e) { /* */ }
  }

  // ---- PHF · Yards map interactivity -----------------------------------
  // Clicking a yard in the list (or a pin on the map) marks both as active
  // and updates the bottom detail strip. Pins pulse subtly when active.
  // Keyboard: arrow keys cycle the active yard, Home/End jump to first/last.
  function setupYards() {
    var roots = document.querySelectorAll('[data-phf-yards]');
    if (!roots.length) return;

    roots.forEach(function (root) {
      var items   = root.querySelectorAll('[data-phf-yard]');
      var pins    = root.querySelectorAll('[data-phf-pin]');
      var detail  = root.querySelector('[data-phf-yards-detail]');
      var dataEl  = root.querySelector('script[data-phf-yards-data]');
      if (!items.length) return;

      var data = [];
      if (dataEl) {
        try { data = JSON.parse(dataEl.textContent); } catch (e) { data = []; }
      }

      function setActive(idx) {
        idx = Math.max(0, Math.min(items.length - 1, idx));
        items.forEach(function (el, i) {
          var match = (i === idx);
          el.classList.toggle('is-active', match);
          el.setAttribute('aria-selected', match ? 'true' : 'false');
          el.setAttribute('tabindex', match ? '0' : '-1');
        });
        pins.forEach(function (el, i) {
          el.classList.toggle('is-active', i === idx);
        });
        if (detail && data[idx]) {
          var row = data[idx];
          var fields = {
            '[data-phf-detail-name]':  row.name,
            '[data-phf-detail-elev]':  row.elevation,
            '[data-phf-detail-yield]': row['yield']
          };
          Object.keys(fields).forEach(function (sel) {
            var el = detail.querySelector(sel);
            if (el) el.textContent = fields[sel] || '';
          });
        }
      }

      items.forEach(function (item, i) {
        item.addEventListener('click', function () { setActive(i); });
        item.addEventListener('keydown', function (e) {
          switch (e.key) {
            case 'ArrowDown':
            case 'ArrowRight':
              e.preventDefault();
              setActive((i + 1) % items.length);
              items[(i + 1) % items.length].focus();
              break;
            case 'ArrowUp':
            case 'ArrowLeft':
              e.preventDefault();
              setActive((i - 1 + items.length) % items.length);
              items[(i - 1 + items.length) % items.length].focus();
              break;
            case 'Home':
              e.preventDefault();
              setActive(0);
              items[0].focus();
              break;
            case 'End':
              e.preventDefault();
              setActive(items.length - 1);
              items[items.length - 1].focus();
              break;
            case 'Enter':
            case ' ':
              e.preventDefault();
              setActive(i);
              break;
          }
        });
      });

      pins.forEach(function (pin, i) {
        pin.addEventListener('click', function () {
          setActive(i);
          // Scroll the corresponding list item into view if it's off-screen
          if (items[i]) {
            items[i].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        });
      });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
